import { useState } from "react";

import type { TenantUserOut } from "@/api/tenants";
import { Button } from "@/components/ui";
import { t } from "@/i18n";

type TenantRole = "tenant_admin" | "tenant_user";

interface PendingListProps {
  pending: TenantUserOut[];
  onApprove: (userId: number, role: TenantRole) => void;
  onReject: (userId: number, reason: string) => void;
}

/** Bekleyen uyelik onaylari — /kullanicilar (gorev talimati madde 4:
 * "onayla/reddet+neden"). Reddet iki adimli: once neden alani acilir.
 * Onay rolu secilebilir (eski legacy/admin/pending-users.html:68 "approveRole"
 * secici ile esitlik — orada da varsayilan en dusuk yetkiliydi/"viewer").
 * Varsayilan tenant_user; "Onayla" secili rolle {status:"active", role}
 * gonderir (bkz. Kullanicilar.tsx handleApprove). */
export function PendingList({ pending, onApprove, onReject }: PendingListProps) {
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [roleByUserId, setRoleByUserId] = useState<Record<number, TenantRole>>({});

  if (pending.length === 0) {
    return <p className="text-sm text-muted">{t("kullanicilar.pending.empty")}</p>;
  }

  return (
    <div className="card space-y-3">
      {pending.map((user, i) => {
        const role = roleByUserId[user.user_id] ?? "tenant_user";
        return (
          <div
            key={user.user_id}
            className={`pt-3 first:pt-0 ${i > 0 ? "border-t-[1.5px] border-line" : ""}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-ink">{user.email}</p>
                <p className="text-xs text-muted">
                  {user.first_name} {user.last_name}
                </p>
              </div>
              {rejectingId !== user.user_id && (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    aria-label={`${t("kullanicilar.table.role")} — ${user.email}`}
                    value={role}
                    onChange={(e) =>
                      setRoleByUserId((prev) => ({ ...prev, [user.user_id]: e.target.value as TenantRole }))
                    }
                    className="input !w-auto"
                  >
                    <option value="tenant_user">{t("kullanicilar.role.tenant_user")}</option>
                    <option value="tenant_admin">{t("kullanicilar.role.tenant_admin")}</option>
                  </select>
                  <Button type="button" variant="primary" size="sm" onClick={() => onApprove(user.user_id, role)}>
                    {t("kullanicilar.approve")}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setRejectingId(user.user_id);
                      setReason("");
                    }}
                  >
                    {t("kullanicilar.reject")}
                  </Button>
                </div>
              )}
            </div>

            {rejectingId === user.user_id && (
              <div className="mt-2 space-y-2">
                <label className="block">
                  <span className="label">{t("kullanicilar.reject.reason_label")}</span>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("kullanicilar.reject.reason_placeholder")}
                    className="input !min-h-0 py-2"
                    rows={2}
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      onReject(user.user_id, reason.trim());
                      setRejectingId(null);
                    }}
                  >
                    {t("kullanicilar.reject.confirm")}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setRejectingId(null)}>
                    {t("kullanicilar.reject.cancel")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
